/**
 * The brand marks the design uses to name a channel or a source — full-colour
 * SVGs extracted from the approved page, not a monochrome icon set. They are
 * nominative trademark use: never recolour them, and never give them alt text,
 * because the adjacent label already names the tool.
 */
/**
 * One entry per mark, carrying both halves. Two parallel records would have to
 * be kept in step by hand, and the failure mode is a 404 at runtime rather than
 * a compile error.
 */
const ICONS = {
  gmail: { file: "gmail", label: "Gmail" },
  googleanalytics: { file: "google-analytics", label: "Google Analytics" },
  googlecalendar: { file: "google-calendar", label: "Google Calendar" },
  googledocs: { file: "google-docs", label: "Google Docs" },
  googledrive: { file: "google-drive", label: "Google Drive" },
  googlesheets: { file: "google-sheets", label: "Google Sheets" },
  hubspot: { file: "hubspot", label: "HubSpot" },
  microsoft: { file: "microsoft-365", label: "Microsoft 365" },
  notion: { file: "notion", label: "Notion" },
  slack: { file: "slack", label: "Slack" },
  whatsapp: { file: "whatsapp", label: "WhatsApp Business" },
} as const;

export type AppIconKey = keyof typeof ICONS;

/** The name of each tool, for callers that print it beside the mark. */
export const APP_ICONS: Record<AppIconKey, string> = Object.fromEntries(
  Object.entries(ICONS).map(([key, icon]) => [key, icon.label])
) as Record<AppIconKey, string>;

interface AppIconProps {
  /** Set only when no adjacent text names the tool. */
  labelled?: boolean;
  name: AppIconKey;
}

export function AppIcon({ labelled = false, name }: Readonly<AppIconProps>) {
  return (
    // No `loading="lazy"`: these are 20px vectors, several of them sit inside
    // the home page's opening slab, and deferring an above-the-fold image only
    // delays it. There is nothing to save on a file this small.
    <img
      alt={labelled ? ICONS[name].label : ""}
      className={`app-icon ${name}`}
      height={20}
      src={`/images/brands/${ICONS[name].file}.svg`}
      width={20}
    />
  );
}
